import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  sendOtp,
  verifyOtp,
  resetPassword,
  clearAuthFeedback,
} from "../../features/auth/authSlice";
import type { RootState, AppDispatch } from "../../app/store";
import "../Login/Login.css";
import "./ForgotPassword.css";
import PasswordInput from "../../components/PasswordInput";

type Phase = "request" | "verify" | "reset";

const phaseTitles: Record<Phase, string> = {
  request: "Forgot Password",
  verify: "Verify OTP",
  reset: "Reset Password",
};

const ForgotPassword: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const { status, message, error } = useSelector((state: RootState) => state.auth);

  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [localError, setLocalError] = useState<string | null>(null);
  const [phase, setPhase] = useState<Phase>("request");

  useEffect(() => {
    if (message || error) {
      const timer = setTimeout(() => dispatch(clearAuthFeedback()), 5000);
      return () => clearTimeout(timer);
    }
  }, [message, error, dispatch]);

  const handleRequestOtp = (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);
    dispatch(sendOtp(email)).then((resultAction) => {
      if (sendOtp.fulfilled.match(resultAction)) {
        setOtp("");
        setPhase("verify");
      }
    });
  };

  const handleVerifyOtp = (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);
    dispatch(verifyOtp({ email, otp })).then((resultAction) => {
      if (verifyOtp.fulfilled.match(resultAction)) {
        setNewPassword("");
        setConfirmPassword("");
        setPhase("reset");
      }
    });
  };

  const handleResendOtp = () => {
    setLocalError(null);
    dispatch(sendOtp(email)).then((resultAction) => {
      if (sendOtp.fulfilled.match(resultAction)) {
        setOtp("");
      }
    });
  };

  const handleReset = (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);

    if (newPassword !== confirmPassword) {
      setLocalError("Passwords do not match.");
      return;
    }

    dispatch(resetPassword({ email, newPassword })).then((resultAction) => {
      if (resetPassword.fulfilled.match(resultAction)) {
        setTimeout(() => navigate("/"), 1800);
      }
    });
  };

  const handleBack = () => {
    setLocalError(null);
    dispatch(clearAuthFeedback());

    if (phase === "verify") {
      setPhase("request");
      setOtp("");
      return;
    }

    if (phase === "reset") {
      setPhase("verify");
      setNewPassword("");
      setConfirmPassword("");
      return;
    }

    navigate("/");
  };

  const displayError = localError || error;

  return (
    <div className="login-container">
      <div className="login-card">
        <h2 className="login-title">{phaseTitles[phase]}</h2>

        {(message || displayError) && (
          <p
            style={{
              textAlign: "center",
              color: displayError ? "#e53e3e" : "#38a169",
              marginBottom: "1.2rem",
            }}
          >
            {displayError || message}
          </p>
        )}

        {phase === "request" && (
          <form className="forgotCard" onSubmit={handleRequestOtp}>
            <input
              className="login-input"
              type="email"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value.trim())}
              required
              disabled={status === "loading"}
            />

            <button
              className="login-button otpBtn"
              type="submit"
              disabled={status === "loading"}
            >
              {status === "loading" ? "Sending..." : "Send OTP"}
            </button>
          </form>
        )}

        {phase === "verify" && (
          <form className="forgotCard" onSubmit={handleVerifyOtp}>
            <p style={{ textAlign: "center", margin: "0 0 1rem 0" }}>
              Enter the 6-digit code sent to <strong>{email}</strong>
            </p>

            <input
              className="login-input"
              placeholder="6-digit OTP"
              value={otp}
              onChange={(e) => setOtp(e.target.value.trim())}
              required
              maxLength={6}
              inputMode="numeric"
              pattern="[0-9]{6}"
              disabled={status === "loading"}
            />

            <button
              className="login-button otpBtn"
              type="submit"
              disabled={status === "loading"}
            >
              {status === "loading" ? "Verifying..." : "Verify OTP"}
            </button>

            <button
              type="button"
              className="forgot-link"
              style={{ marginTop: "12px", background: "none", border: "none", cursor: "pointer" }}
              onClick={handleResendOtp}
              disabled={status === "loading"}
            >
              Resend OTP
            </button>
          </form>
        )}

        {phase === "reset" && (
          <form className="resetForm" onSubmit={handleReset}>
            <p style={{ textAlign: "center", margin: "0 0 1rem 0" }}>
              OTP verified. Set a new password for <strong>{email}</strong>
            </p>

            <PasswordInput
              className="login-input"
              placeholder="New password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              disabled={status === "loading"}
            />

            <PasswordInput
              className="login-input otpBtn"
              placeholder="Confirm new password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              disabled={status === "loading"}
            />

            <button
              className="login-button otpBtn"
              type="submit"
              disabled={status === "loading"}
            >
              {status === "loading" ? "Resetting..." : "Reset Password"}
            </button>
          </form>
        )}

        <p className="back" onClick={handleBack}>
          {phase === "request" ? "← Back to Login" : "← Back"}
        </p>
      </div>
    </div>
  );
};

export default ForgotPassword;
